import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import BuyerPublicNavbar from "@/components/BuyerPublicNavbar";
import Footer from "@/components/Footer";
import {
  ArrowRight,
  EnvelopeSimple,
  Factory,
  Handshake,
  Lightning,
  MapPin,
  Phone,
  RocketLaunch,
  ShieldCheck,
  Storefront,
  Truck,
  UsersThree,
} from "@phosphor-icons/react";

const faqs = [
  {
    q: "What is SCAFFO India?",
    a: "SCAFFO India is a digital marketplace that connects buyers with verified scaffolding suppliers. We are starting with scaffolding in Gurgaon and will gradually expand to more cities and categories like cement, TMT bars, JCB rental, and transporter services.",
  },
  {
    q: "Which cities does SCAFFO India currently serve?",
    a: "We have launched in Gurgaon and are actively building our supplier and buyer base here. We will be expanding to other cities across India in the coming months.",
  },
  {
    q: "What products can I source through SCAFFO India today?",
    a: "Right now, we focus on scaffolding. We will be adding cement, TMT bars, JCB rental, and transporter services soon.",
  },
  {
    q: "How quickly will I receive a response after submitting an enquiry?",
    a: "Our team typically responds within 24 hours on business days. For urgent requirements, please call us at +91 89208 93912.",
  },
  {
    q: "Is there any charge for buyers?",
    a: "No. Posting requirements and receiving supplier quotes is completely free for buyers.",
  },
  {
    q: "I am a supplier — how do I list my business?",
    a: "You can sign up directly on our website through the Supplier Registration page. The process is quick and self-serve. If you need help during registration, call us at +91 89208 93912 or email Scaffoindia@gmail.com.",
  },
  {
    q: "How does SCAFFO India verify its suppliers?",
    a: "Every supplier goes through a verification process that includes business registration checks, GST validation, and ongoing quality scoring based on buyer feedback.",
  },
  {
    q: "Can I get a customized quote for bulk orders?",
    a: "Yes. Share your requirement through our contact form or call us directly. We will connect you with verified suppliers in Gurgaon who can offer competitive pricing.",
  },
];

const stats = [
  { target: 500, suffix: "+", label: "Active users" },
  { target: 500, suffix: "+", label: "Leads delivered" },
  { target: 20, suffix: "+", label: "Cities" },
  { target: 24, suffix: "hr", label: "Avg response" },
];

function useCountUp(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

const howItWorks = [
  {
    step: "01",
    title: "Post your requirement",
    description: "Select scaffolding materials from our catalog or add custom items. Add project details, site location, and timeline.",
  },
  {
    step: "02",
    title: "Get matched with suppliers",
    description: "We route your requirement to verified suppliers in your city who supply what you need on your timeline.",
  },
  {
    step: "03",
    title: "Receive competitive quotes",
    description: "Suppliers review your requirement and submit detailed quotes. Compare pricing, delivery terms, and ratings.",
  },
  {
    step: "04",
    title: "Choose and proceed",
    description: "Select the best quote for your project. We'll connect you directly with the supplier to finalize.",
  },
];

const benefits = [
  { icon: Factory, title: "Quality scaffolding", description: "Certified materials from vetted suppliers who meet industry standards." },
  { icon: Truck, title: "Logistics support", description: "Suppliers handle delivery and pickup so you can focus on construction." },
  { icon: Handshake, title: "Competitive pricing", description: "Multiple suppliers compete for your business — real market rates." },
  { icon: RocketLaunch, title: "Fast turnaround", description: "Structured requirements mean faster quotes. Responses within hours." },
];

const pillars = [
  {
    icon: UsersThree,
    title: "For buyers",
    description: "Pick materials from our catalog, review everything in one enquiry, and attach project details before you submit. No spreadsheets. No endless calls.",
  },
  {
    icon: ShieldCheck,
    title: "Verified network",
    description: "Suppliers are onboarded with consistent profiles — what they supply, where they operate, and how they engage with RFQs.",
  },
  {
    icon: MapPin,
    title: "City-aware sourcing",
    description: "Requirements reflect where the work happens. Suppliers respond with logistics and lead times that match your actual site.",
  },
  {
    icon: Lightning,
    title: "Fast path to quotes",
    description: "From Select materials to submitted enquiry in one sitting — or save as you go. The flow is designed for how you actually work.",
  },
];

function FaqItem({ item, idx }) {
  const [open, setOpen] = useState(false);
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(open ? contentRef.current.scrollHeight : 0);
    }
  }, [open]);

  return (
    <div style={{ borderColor: "#E0D9CF" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-5 sm:gap-8 py-6 text-left group focus:outline-none"
      >
        {/* Index */}
        <span
          className="shrink-0 font-black tabular-nums leading-none mt-0.5 transition-colors duration-200"
          style={{
            fontSize: "clamp(0.95rem, 1.5vw, 1.05rem)",
            color: open ? "#FF7A00" : "#C4B9A8",
            minWidth: "2rem",
          }}
        >
          {String(idx + 1).padStart(2, "0")}
        </span>

        {/* Question */}
        <span
          className="flex-1 font-bold leading-snug transition-colors duration-200"
          style={{
            fontSize: "clamp(0.95rem, 1.5vw, 1.05rem)",
            color: open ? "#111" : "#333",
          }}
        >
          {item.q}
        </span>

        {/* Toggle indicator */}
        <span
          className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5 transition-all duration-200"
          style={{
            background: open ? "#FF7A00" : "#E8E3D9",
            color: open ? "#fff" : "#888",
            transform: open ? "rotate(45deg)" : "rotate(0deg)",
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <line x1="6" y1="1" x2="6" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <line x1="1" y1="6" x2="11" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
      </button>

      {/* Answer */}
      <div
        style={{
          height: `${height}px`,
          overflow: "hidden",
          transition: "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div ref={contentRef} className="pb-6 pl-[calc(2rem+1.25rem)] sm:pl-[calc(2rem+2rem)]">
          <p className="text-sm leading-relaxed" style={{ color: "#666" }}>{item.a}</p>
        </div>
      </div>
    </div>
  );
}

function StatNumber({ target, suffix, label, started, index }) {
  const count = useCountUp(target, 1600 + index * 120, started);
  return (
    <div
      className="flex flex-col justify-center p-8 sm:p-10"
      style={{
        borderRight: index % 2 === 0 ? "1px solid #2a2a2a" : "none",
        borderBottom: index < 2 ? "1px solid #2a2a2a" : "none",
        minHeight: "140px",
      }}
    >
      <p
        className="font-black leading-none tracking-tighter tabular-nums"
        style={{ fontSize: "clamp(2.4rem, 4.5vw, 3.8rem)", color: "#FF7A00" }}
      >
        {count}{suffix}
      </p>
      <p className="mt-2 text-sm font-medium" style={{ color: "#888" }}>{label}</p>
    </div>
  );
}

export default function AboutUsPage() {
  const statsRef = useRef(null);
  const [statsStarted, setStatsStarted] = useState(false);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStatsStarted(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#F7F4EF" }}>
      <BuyerPublicNavbar />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <div
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #111 0%, #1e1209 55%, #2d1a08 100%)" }}
      >
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(#FF7A00 1px, transparent 1px), linear-gradient(90deg, #FF7A00 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF7A00]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch">

            {/* Left */}
            <div className="py-16 sm:py-20 lg:py-28 lg:pr-16 border-b lg:border-b-0 lg:border-r" style={{ borderColor: "#2a2a2a" }}>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-7"
                style={{ background: "rgba(255,122,0,0.15)", color: "#FF7A00", border: "1px solid rgba(255,122,0,0.25)" }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF7A00] animate-pulse" />
                About us
              </span>
              <h1
                className="font-black text-white leading-none tracking-tighter mb-6"
                style={{ fontSize: "clamp(3rem, 7vw, 5.5rem)", letterSpacing: "-0.04em" }}
              >
                India's<br />
                scaffolding<br />
                <span style={{ color: "#FF7A00" }}>marketplace.</span>
              </h1>
              <p className="text-neutral-400 text-base sm:text-lg leading-relaxed max-w-md mb-10">
                Connecting project teams with verified scaffolding suppliers through structured requirements, transparent profiles, and competitive quotes.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/materials"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-white transition-all hover:opacity-90"
                  style={{ background: "#FF7A00" }}
                >
                  Post requirement <ArrowRight size={15} weight="bold" />
                </Link>
                <Link
                  to="/contact-us"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all hover:opacity-80"
                  style={{ background: "rgba(255,255,255,0.08)", color: "#fff", border: "1px solid rgba(255,255,255,0.12)" }}
                >
                  <EnvelopeSimple size={15} weight="bold" />
                  Contact us
                </Link>
              </div>
            </div>

            {/* Right — stats */}
            <div
              ref={statsRef}
              className="grid grid-cols-2 self-stretch"
            >
              {stats.map((s, i) => (
                <StatNumber
                  key={s.label}
                  target={s.target}
                  suffix={s.suffix}
                  label={s.label}
                  started={statsStarted}
                  index={i}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── MISSION ──────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 lg:gap-20 items-start">
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#FF7A00" }}>
              Our mission
            </p>
            <div className="w-10 h-1 rounded-full" style={{ background: "#FF7A00" }} />
          </div>
          <div>
            <p
              className="font-black leading-tight tracking-tighter text-neutral-900 mb-6"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)" }}
            >
              Built for every team that moves materials to a site.
            </p>
            <div className="space-y-4 text-neutral-600 text-base leading-relaxed">
              <p>
                Scaffoindia is a dedicated marketplace for scaffolding, shuttering, and related construction equipment. We help project teams capture what they need with a structured catalog, then route requirements to suppliers who are set up to quote quickly and clearly — whether you are renting for a short job or buying material outright.
              </p>
              <p>
                We launched in Gurgaon and are actively growing our verified supplier network. Our roadmap includes more cities and categories: cement, TMT bars, JCB rental, and transporter services.
              </p>
            </div>
            <div
              className="mt-8 flex items-start gap-3 p-5 rounded-2xl"
              style={{ background: "#fff", border: "1px solid #E8E3D9" }}
            >
              <MapPin size={18} weight="fill" className="mt-0.5 shrink-0" style={{ color: "#FF7A00" }} />
              <div>
                <p className="text-xs font-bold tracking-wide uppercase mb-0.5" style={{ color: "#999" }}>Registered office</p>
                <p className="text-sm font-semibold text-neutral-800">Vipul Business Park, Sector 48, Gurugram, Haryana</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ─────────────────────────────────────── */}
      <div style={{ background: "#1a1a1a" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20">
          <div className="flex items-end justify-between mb-12 gap-4">
            <div>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#FF7A00" }}>
                How it works
              </p>
              <h2
                className="font-black text-white leading-tight tracking-tighter"
                style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}
              >
                Four steps to<br />your first quote.
              </h2>
            </div>
            <Link
              to="/materials"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all hover:opacity-90 shrink-0"
              style={{ background: "#FF7A00", color: "#fff" }}
            >
              Start now <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: "#2a2a2a" }}>
            {howItWorks.map((item) => (
              <div key={item.step} className="p-7 sm:p-8" style={{ background: "#1a1a1a" }}>
                <p
                  className="font-black leading-none mb-5"
                  style={{ fontSize: "3rem", color: "rgba(255,122,0,0.2)", letterSpacing: "-0.05em" }}
                >
                  {item.step}
                </p>
                <h3 className="text-base font-black text-white mb-3 leading-snug">{item.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "#888" }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── PILLARS ──────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20">
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#FF7A00" }}>
          What we offer
        </p>
        <h2
          className="font-black text-neutral-900 leading-tight tracking-tighter mb-12"
          style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}
        >
          Built around how<br />procurement actually works.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {pillars.map(({ icon: Icon, title, description }, i) => (
            <div
              key={title}
              className="rounded-2xl p-7 sm:p-8 flex flex-col gap-5"
              style={{ background: "#fff", border: "1px solid #E8E3D9" }}
            >
              <div className="flex items-start gap-5">
                <div
                  className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: "rgba(255,122,0,0.1)" }}
                >
                  <Icon size={22} weight="bold" style={{ color: "#FF7A00" }} />
                </div>
                <div>
                  <p
                    className="font-black text-neutral-900 leading-snug mb-2"
                    style={{ fontSize: "1.05rem" }}
                  >
                    {title}
                  </p>
                  <p className="text-sm text-neutral-500 leading-relaxed">{description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── BENEFITS ─────────────────────────────────────────── */}
      <div style={{ background: "#F0EBE3" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20">
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#FF7A00" }}>
            Why choose us
          </p>
          <h2
            className="font-black text-neutral-900 leading-tight tracking-tighter mb-12"
            style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}
          >
            Benefits for your project.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {benefits.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="rounded-2xl p-6"
                style={{ background: "#FDFCF9", border: "1px solid #E8E3D9" }}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: "rgba(255,122,0,0.1)" }}
                >
                  <Icon size={20} weight="bold" style={{ color: "#FF7A00" }} />
                </div>
                <h3 className="text-sm font-black text-neutral-900 mb-2">{title}</h3>
                <p className="text-sm text-neutral-500 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FOR BUYERS & SUPPLIERS ───────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <div
            className="rounded-2xl p-8 sm:p-10 flex flex-col"
            style={{
              background: "linear-gradient(135deg, #1a1a1a 0%, #2d1a08 100%)",
              border: "1px solid #2a2a2a",
            }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-6"
              style={{ background: "rgba(255,122,0,0.15)" }}
            >
              <UsersThree size={22} weight="bold" style={{ color: "#FF7A00" }} />
            </div>
            <h3 className="text-xl font-black text-white mb-3">For buyers</h3>
            <p className="text-sm leading-relaxed flex-1" style={{ color: "#999" }}>
              Post scaffolding requirements and receive competitive quotes from pre-verified suppliers. No login required to start.
            </p>
            <Link
              to="/materials"
              className="inline-flex items-center gap-2 mt-8 text-sm font-bold transition-opacity hover:opacity-80"
              style={{ color: "#FF7A00" }}
            >
              Browse materials <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          <div
            className="rounded-2xl p-8 sm:p-10 flex flex-col"
            style={{ background: "#fff", border: "1px solid #E8E3D9" }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-6"
              style={{ background: "rgba(255,122,0,0.1)" }}
            >
              <Storefront size={22} weight="bold" style={{ color: "#FF7A00" }} />
            </div>
            <h3 className="text-xl font-black text-neutral-900 mb-3">For suppliers</h3>
            <p className="text-sm text-neutral-500 leading-relaxed flex-1">
              Grow your scaffolding business with quality leads from buyers who have structured, actionable requirements. Sign up and start receiving RFQs in your city.
            </p>
            <Link
              to="/supplier/login"
              className="inline-flex items-center gap-2 mt-8 text-sm font-bold transition-opacity hover:opacity-80"
              style={{ color: "#FF7A00" }}
            >
              Supplier registration <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </div>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <div style={{ background: "#F7F4EF" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20">

          {/* Header row */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
            <div>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#FF7A00" }}>
                FAQ
              </p>
              <h2
                className="font-black text-neutral-900 leading-tight tracking-tighter"
                style={{ fontSize: "clamp(2rem, 4.5vw, 3.2rem)" }}
              >
                Common questions.
              </h2>
            </div>
            <Link
              to="/contact-us"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold shrink-0 transition-all hover:opacity-90"
              style={{ background: "#FF7A00", color: "#fff" }}
            >
              Still have questions? <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          {/* FAQ items */}
          <div className="divide-y" style={{ borderColor: "#E0D9CF" }}>
            {faqs.map((item, idx) => (
              <FaqItem key={item.q} item={item} idx={idx} />
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ──────────────────────────────────────────────── */}
      <div
        className="relative overflow-hidden"
        style={{ background: "#FF7A00" }}
      >
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 sm:py-16 flex flex-col sm:flex-row items-center justify-between gap-8">
          <div>
            <h2
              className="font-black text-white leading-tight tracking-tighter mb-2"
              style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.5rem)" }}
            >
              Ready to get started?
            </h2>
            <p className="text-sm" style={{ color: "rgba(255,255,255,0.75)" }}>
              Post your requirement and get quotes from verified suppliers in your city.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Link
              to="/materials"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-black transition-all hover:opacity-90"
              style={{ background: "#fff", color: "#FF7A00" }}
            >
              Post requirement <ArrowRight size={15} weight="bold" />
            </Link>
            <Link
              to="/contact-us"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all hover:opacity-80"
              style={{ background: "rgba(0,0,0,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)" }}
            >
              <Phone size={15} weight="bold" />
              Contact us
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
